import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MessageSquare, Megaphone, Bug, Lightbulb, MessagesSquare, Heart,
  Pin, Trash2, Send, Plus, Loader2, Play,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPE_META = {
  discussion: { label: "Discussion", icon: MessagesSquare, color: "text-blue-400" },
  announcement: { label: "Announcement", icon: Megaphone, color: "text-amber-400" },
  bug: { label: "Bug report", icon: Bug, color: "text-red-400" },
  suggestion: { label: "Suggestion", icon: Lightbulb, color: "text-emerald-400" },
};
const FILTERS = ["all", "discussion", "announcement", "bug", "suggestion"];
const PAGE = 30;

export default function Community() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);

  const load = useCallback((offset = 0) => {
    const params = { offset, limit: PAGE };
    if (filter !== "all") params.type = filter;
    return api.get(`/community/games/${gameId}`, { params })
      .then((r) => {
        setData(r.data);
        setPosts((prev) => (offset > 0 ? [...prev, ...r.data.posts] : r.data.posts));
        setHasMore(!!r.data.has_more);
      })
      .catch(() => setData(false));
  }, [gameId, filter]);
  useEffect(() => { setPosts([]); setHasMore(false); load(0); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try { await load(posts.length); } finally { setLoadingMore(false); }
  };
  const reload = () => load(0);

  if (data === false) return <div className="p-8"><EmptyState emoji="🔍" title="Game not found" action={<Button onClick={() => navigate(-1)} className="rounded-full">Go back</Button>} /></div>;
  if (!data) return <Spinner />;
  const g = data.game;

  return (
    <div className="p-5 lg:p-8 max-w-3xl mx-auto" data-testid="community-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-4" data-testid="community-back"><ArrowLeft className="w-4 h-4" /> Back</button>

      {/* Game header */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 mb-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl grid place-items-center text-3xl bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] shrink-0 overflow-hidden">
          {g.cover_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${g.cover_image}`} alt={g.title} className="w-full h-full object-cover" /> : g.cover_emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold truncate">{g.title}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-3"><span>Community</span><span className="flex items-center gap-1"><Play className="w-3 h-3" /> {g.plays}</span><span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {g.likes}</span></div>
        </div>
        <Button onClick={() => navigate(`/app/arcade/${g.id}`)} variant="outline" className="rounded-full" data-testid="community-play-btn"><Play className="w-4 h-4" /> Play</Button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            {FILTERS.map((f) => (
              <TabsTrigger key={f} value={f} data-testid={`community-filter-${f}`} className="capitalize">
                {f === "all" ? "All" : TYPE_META[f].label}{f !== "all" && data.counts[f] > 0 ? ` (${data.counts[f]})` : ""}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button onClick={() => setCreating(true)} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="new-post-btn"><Plus className="w-4 h-4" /> New post</Button>
      </div>

      {posts.length === 0 ? (
        <EmptyState emoji="💬" title="No posts yet" text="Start the conversation — ask a question, report a bug or share an idea!" />
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} canManage={data.can_manage} me={user} onChange={reload} />
          ))}
          {hasMore && (
            <div className="pt-2 text-center">
              <Button onClick={loadMore} disabled={loadingMore} variant="outline" className="rounded-full" data-testid="community-load-more">
                {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Load more
              </Button>
            </div>
          )}
        </div>
      )}

      <NewPostDialog open={creating} gameId={gameId} canManage={data.can_manage} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); reload(); }} />
    </div>
  );
}

function PostCard({ post, canManage, me, onChange }) {
  const meta = TYPE_META[post.type] || TYPE_META.discussion;
  const Icon = meta.icon;
  const [open, setOpen] = useState(false);
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.like_count);
  const [reply, setReply] = useState("");
  const [replies, setReplies] = useState(post.replies || []);
  const [sending, setSending] = useState(false);
  const canDelete = canManage || post.author_id === me?.id;

  const like = async () => {
    try { const { data } = await api.post(`/community/posts/${post.id}/like`); setLiked(data.liked); setLikes(data.like_count); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try { const { data } = await api.post(`/community/posts/${post.id}/reply`, { body: reply }); setReplies(data.replies); setReply(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const pin = async () => { try { await api.post(`/community/posts/${post.id}/pin`); onChange(); } catch { /* ignore */ } };
  const del = async () => { if (!window.confirm("Delete this post?")) return; try { await api.delete(`/community/posts/${post.id}`); onChange(); toast.success("Deleted"); } catch { toast.error("Could not delete"); } };

  return (
    <div className={`rounded-2xl border bg-[hsl(var(--card))] p-4 ${post.pinned ? "border-[hsl(var(--primary))]/50" : "border-[hsl(var(--border))]"}`} data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full grid place-items-center text-lg bg-gradient-to-br from-[#7c3aed] to-[#3b9eff] shrink-0">{post.author_avatar || "🎮"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className={`gap-1 text-[10px] ${meta.color}`}><Icon className="w-3 h-3" /> {meta.label}</Badge>
            {post.pinned && <Badge className="gap-1 text-[10px] bg-[hsl(var(--primary))]"><Pin className="w-3 h-3" /> Pinned</Badge>}
            <span className="text-xs text-[hsl(var(--muted-foreground))]">by {post.author_name}</span>
            {post.author_is_supporter && (
              <Badge className="gap-1 text-[10px] bg-emerald-600 hover:bg-emerald-600 text-white" data-testid={`supporter-badge-${post.id}`}>
                <Heart className="w-3 h-3 fill-white" /> Supporter
              </Badge>
            )}
          </div>
          <h3 className="font-bold mt-1.5">{post.title}</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5 whitespace-pre-wrap">{post.body}</p>
          {post.image_url && (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${post.image_url}`} alt="Robot score card"
              className="mt-3 w-full max-w-md rounded-xl border border-[hsl(var(--border))]" data-testid={`post-image-${post.id}`} />
          )}
          <div className="flex items-center gap-3 mt-3 text-xs">
            <button onClick={like} className={`flex items-center gap-1 ${liked ? "text-red-500" : "text-[hsl(var(--muted-foreground))]"}`} data-testid={`like-${post.id}`}><Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} /> {likes}</button>
            <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]" data-testid={`replies-${post.id}`}><MessageSquare className="w-4 h-4" /> {replies.length}</button>
            {canManage && <button onClick={pin} className="flex items-center gap-1 text-[hsl(var(--muted-foreground))]" data-testid={`pin-${post.id}`}><Pin className="w-4 h-4" /> {post.pinned ? "Unpin" : "Pin"}</button>}
            {canDelete && <button onClick={del} className="flex items-center gap-1 text-red-500" data-testid={`delete-${post.id}`}><Trash2 className="w-4 h-4" /></button>}
          </div>

          {open && (
            <div className="mt-3 space-y-2 border-t border-[hsl(var(--border))] pt-3" data-testid={`replies-panel-${post.id}`}>
              {replies.map((r) => (
                <div key={r.id} className="flex items-start gap-2 text-sm">
                  <div className="w-6 h-6 rounded-full grid place-items-center text-xs bg-[hsl(var(--secondary))] shrink-0">{r.author_avatar || "🎮"}</div>
                  <div><span className="font-semibold text-xs">{r.author_name}</span>{r.author_is_supporter && <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] text-emerald-400" data-testid="reply-supporter-badge"><Heart className="w-2.5 h-2.5 fill-emerald-400" /> Supporter</span>}<p className="text-[hsl(var(--muted-foreground))] whitespace-pre-wrap">{r.body}</p></div>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Write a reply…" data-testid={`reply-input-${post.id}`} />
                <Button onClick={sendReply} disabled={sending || !reply.trim()} size="sm" className="rounded-full shrink-0" data-testid={`reply-send-${post.id}`}>{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewPostDialog({ open, gameId, canManage, onClose, onCreated }) {
  const [type, setType] = useState("discussion");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setType("discussion"); setTitle(""); setBody(""); } }, [open]);
  const types = Object.keys(TYPE_META).filter((t) => t !== "announcement" || canManage);

  const submit = async () => {
    if (title.trim().length < 2 || !body.trim()) { toast.error("Add a title and a message."); return; }
    setSaving(true);
    try { await api.post(`/community/games/${gameId}/posts`, { type, title, body }); toast.success("Posted! 🎉"); onCreated(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-post-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[hsl(var(--primary))]" /> New community post</DialogTitle>
          <DialogDescription>Share with this game's community. Be kind and keep it on-topic.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="post-type"><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{TYPE_META[t].label}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="post-title" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="What's on your mind?" data-testid="post-body" />
          <Button onClick={submit} disabled={saving} className="w-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="post-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
